import {
  Button,
  IconButton,
  Input,
  Select,
  TextArea,
} from '@servicecompressionllc/sc-design-system';
import { Trash2 } from 'lucide-react';
import type { DeclaredField } from '../../lib/types.js';
import { humanizeKey } from '../workflowBuilder/definitionEdit.js';

const typeLabels: Record<DeclaredField['type'], string> = {
  string: 'Text',
  number: 'Number',
  boolean: 'Yes / No',
  date: 'Date',
  array: 'List',
  object: 'Object',
  null: 'Empty',
};

interface Props {
  fields: DeclaredField[];
  editingField: string | null;
  onEdit: (name: string | null) => void;
  onChange: (name: string, patch: Partial<DeclaredField>) => void;
  onRemove: (name: string) => void;
}

export const EventFieldList = ({ fields, editingField, onEdit, onChange, onRemove }: Props) => (
  <div className="mt-3 overflow-hidden rounded-lg border border-outline bg-surface">
    <div className="hidden items-center gap-3 border-b border-outline px-4 py-2 text-xs font-medium text-ink-muted sm:flex">
      <div className="grid min-w-0 flex-1 grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,2fr)] gap-4">
        <span>Field</span>
        <span>Type</span>
        <span>Predefined values</span>
      </div>
      <span className="w-16 shrink-0">
        <span className="sr-only">Actions</span>
      </span>
    </div>
    {fields.map((field) => {
      const label = field.label?.trim() || humanizeKey(field.name);
      const expanded = editingField === field.name;
      const preview = field.values?.find((value) => value.trim())?.trim();
      return (
        <div key={field.name} className="border-b border-outline last:border-b-0">
          <div className="flex items-center gap-3 p-3 sm:px-4">
            <div className="grid min-w-0 flex-1 gap-1 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,2fr)] sm:items-center sm:gap-4">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink" title={label}>
                  {label}
                </p>
              </div>
              <span className="text-xs text-ink-muted">{typeLabels[field.type]}</span>
              <span className="truncate text-xs text-ink-muted" title={field.values?.join(', ')}>
                {field.values
                  ? field.values.filter((value) => value.trim()).join(', ') || 'Not set'
                  : '—'}
              </span>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="w-16 shrink-0 whitespace-nowrap"
              aria-label={`${expanded ? 'Close' : 'Edit'} ${field.name}`}
              aria-expanded={expanded}
              onClick={() => onEdit(expanded ? null : field.name)}
            >
              {expanded ? 'Close' : 'Edit'}
            </Button>
          </div>
          {expanded && (
            <div
              className="grid gap-4 border-t border-outline p-4"
              role="group"
              aria-label={`Edit ${field.name} settings`}
            >
              <p className="text-xs text-ink-muted">
                Payload key: <code className="break-all">{field.name}</code>
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="text-sm text-ink-muted">
                  Display name
                  <Input
                    aria-label={`${field.name} display label`}
                    className="mt-1"
                    value={field.label ?? humanizeKey(field.name).slice(0, 80)}
                    maxLength={80}
                    onChange={(e) => onChange(field.name, { label: e.target.value })}
                  />
                </label>
                <label className="text-sm text-ink-muted">
                  Type
                  <Select
                    aria-label={`${field.name} type`}
                    className="mt-1"
                    searchable={false}
                    options={Object.entries(typeLabels).map(([value, label]) => ({ value, label }))}
                    value={[field.type]}
                    onChange={(value) =>
                      onChange(field.name, {
                        type: value[0] as DeclaredField['type'],
                        values: undefined,
                      })
                    }
                  />
                </label>
              </div>
              {field.type === 'string' &&
                (field.values ? (
                  <div>
                    <div className="flex items-center justify-between gap-3">
                      <label
                        htmlFor={`choices-${field.name}`}
                        className="text-sm font-medium text-ink"
                      >
                        Predefined values
                      </label>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => onChange(field.name, { values: undefined })}
                      >
                        Remove predefined values
                      </Button>
                    </div>
                    <p className="mt-1 text-xs text-ink-muted">
                      One value per line, matching what the sender sends. These guide workflow
                      conditions; other incoming values are still accepted.
                    </p>
                    <TextArea
                      id={`choices-${field.name}`}
                      aria-label={`${field.name} choices`}
                      className="mt-2"
                      value={field.values.join('\n')}
                      placeholder={'low\nmedium\nhigh'}
                      onChange={(e) => onChange(field.name, { values: e.target.value.split('\n') })}
                    />
                    {preview && (
                      <div className="mt-3 rounded-lg border border-outline bg-surface-elevated p-3">
                        <p className="text-xs text-ink-subtle">Condition preview</p>
                        <p className="mt-1 break-words text-sm text-ink">
                          {label} → is → {preview}
                        </p>
                      </div>
                    )}
                  </div>
                ) : null)}
              <div className="flex items-center justify-between gap-3">
                {field.type === 'string' && !field.values && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => onChange(field.name, { values: [''] })}
                  >
                    Add predefined values
                  </Button>
                )}
                <IconButton
                  type="button"
                  aria-label={`Remove ${field.name}`}
                  className="ml-auto shrink-0"
                  size="sm"
                  onClick={() => onRemove(field.name)}
                >
                  <Trash2 />
                </IconButton>
              </div>
            </div>
          )}
        </div>
      );
    })}
  </div>
);
